import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Col, Row, Space, Statistic, Tag, Typography, message } from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import GxTable from "../../components/GxTable";
import GxNote from "../../components/GxNote";
import ExportButton from "../../components/ExportButton";
import {
  getReportDebtors, getReportProductionOutput, getReportSalaryPayments,
} from "../../api/client";
import { apiError } from "../../utils/apiError";
import { faNum } from "../../utils/faNumberFormat";

const { Text } = Typography;
/**
 * گزارش‌ها — داخلِ خودِ حساب و کتاب، نه یک صفحهٔ جدا.
 *
 * چرا: هر گزارشی که اینجا هست جوابِ سؤالی است که همین‌جا پیش می‌آید («چقدر
 * طلبکاریم؟»، «این ماه چقدر باید بپردازیم؟»). فرستادنِ کاربر به صفحهٔ دیگر
 * یعنی گم‌کردنِ دوره و فیلترهایی که همین‌جا انتخاب کرده.
 */
export default function FinanceReportsTab({ periodId, periodSelector }) {
  const [debtors, setDebtors] = useState([]);
  const [salary, setSalary] = useState({ rows: [], total_payable: 0 });
  const [prod, setProd] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!periodId) return;
    setLoading(true);
    try {
      const [d, s, p] = await Promise.all([
        getReportDebtors(periodId), getReportSalaryPayments(periodId),
        getReportProductionOutput(),
      ]);
      setDebtors(d.data.rows || []);
      setSalary(s.data || { rows: [], total_payable: 0 });
      setProd(p.data.rows || []);
    } catch (e) { message.error(apiError(e, "خطا در بارگذاری گزارش‌ها")); }
    setLoading(false);
  }, [periodId]);
  useEffect(() => { load(); }, [load]);

  const money = (v) => faNum(Number(v || 0));
  const totalDebt = debtors.reduce((a, r) => a + Number(r.amount || r.balance || 0), 0);

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Card size="small" title="گزارش‌های این دوره" extra={periodSelector}>
        <GxNote id="fin-reports" signature="v1"
          message="همهٔ گزارش‌های مالی همین‌جاست — دیگر لازم نیست به صفحهٔ گزارش‌ها بروید">
          هر جدول دکمهٔ خروجیِ اکسلِ خودش را دارد. «بدهکاران» ماندهٔ همین لحظه است،
          «پرداختِ حقوق» مبلغی که برای این دوره باید پرداخت شود.
        </GxNote>
        <Row gutter={[12, 12]}>
          <Col xs={12} md={8}>
            <Card size="small"><Statistic title="جمعِ بدهکاری (ریال)"
              value={money(totalDebt)} valueStyle={{ color: "#C0392B" }} /></Card>
          </Col>
          <Col xs={12} md={8}>
            <Card size="small"><Statistic title="قابلِ پرداختِ این دوره (ریال)"
              value={money(salary.total_payable)} valueStyle={{ color: "#107C41" }} /></Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small"><Statistic title="تعدادِ بدهکار" value={faNum(debtors.length)} /></Card>
          </Col>
        </Row>
      </Card>

      <Card size="small" title="بدهکاران"
        extra={<ExportButton url="/api/v1/export/debtors" params={{ period_id: periodId }} label="خروجی اکسل" />}>
        <GxTable layoutId="fin-rep-debtors" dataSource={debtors} rowKey={(r, i) => r.employee_id || i}
          size="small" loading={loading}
          columns={[
            { title: "پرسنل", dataIndex: "employee_name" },
            { title: "کد", dataIndex: "employee_code", width: 80 },
            { title: "مبلغ (ریال)", dataIndex: "amount", align: "left",
              render: (v, r) => <b style={{ color: "#C0392B" }}>{money(v ?? r.balance)}</b> },
          ]} />
      </Card>

      <Card size="small" title="پرداختِ حقوقِ دوره"
        extra={<ExportButton url="/api/v1/export/payment"
          params={{ period_id: periodId }} label="خروجی اکسل" />}>
        <GxTable layoutId="fin-rep-salary" dataSource={salary.rows || []}
          rowKey={(r, i) => r.employee_id || i} size="small" loading={loading}
          columns={[
            { title: "پرسنل", dataIndex: "employee_name" },
            { title: "خالص (ریال)", dataIndex: "net_pay", align: "left",
              render: (v) => money(v) },
            { title: "بانک", dataIndex: "bank_name", render: (v) => v || "—" },
            { title: "شبا", dataIndex: "iban", render: (v) => v || "—" },
          ]} />
      </Card>

      <Card size="small" title="خروجیِ تولید"
        extra={<ExportButton url="/api/v1/export/production-summary" params={{ period_id: periodId }} label="خروجی اکسل" />}>
        <GxTable layoutId="fin-rep-prod" dataSource={prod} rowKey={(r, i) => r.order_number || i}
          size="small" loading={loading}
          columns={[
            { title: "کد تولید", dataIndex: "cut_code", width: 100 },
            { title: "محصول", dataIndex: "product_type" },
            { title: "واردشده", dataIndex: "entered", align: "center" },
            { title: "کلِ قطعات", dataIndex: "total_pieces", align: "center",
              render: (v) => faNum(v) },
          ]} />
      </Card>

      <Space wrap>
        <Text type="secondary" style={{ fontSize: 12 }}>خروجی‌های بیشتر:</Text>
        <ExportButton url="/api/v1/export/advances" params={{ period_id: periodId }}
          label="مساعده‌ها" icon={<FileExcelOutlined />} />
        <ExportButton url="/api/v1/export/deductions" params={{ period_id: periodId }}
          label="کسورات" icon={<FileExcelOutlined />} />
        <ExportButton url="/api/v1/export/insurance" params={{ period_id: periodId }}
          label="بیمه" icon={<FileExcelOutlined />} />
      </Space>
    </Space>
  );
}
